export const PRINT_JOB_QUALITY_FILE_TYPES = {
  Preview: 'preview',
  Master: 'master',
  A11y: 'a11y',
};

export const PRINT_JOB_QUALITY_LABEL_KEYS = {
  Preview: 'download_preview_pdf',
  Master: 'download_print_pdf',
  A11y: 'download_accessibility_pdf',
};

export const PRINT_JOB_DOWNLOAD_ENTITIES = {
  edition: 'EditionBundle\\Document\\Edition',
  orderItem: 'OrderBundle\\Document\\EmbeddedOrderItem',
};

export function getNotificationDocIdentifier(notification) {
  return notification?.entityDocIdentifier || notification?.entity_doc_identifier;
}

export function getNotificationEntityId(notification) {
  return notification?.entityId || notification?.entity_id;
}

export function normalizePrintJobUploads(uploads) {
  if (typeof uploads === 'string' && uploads.length > 0) {
    return uploads;
  }

  if (!Array.isArray(uploads)) {
    return null;
  }

  const cleanUploads = uploads.filter(upload => typeof upload === 'string' && upload.length > 0);
  if (cleanUploads.length === 0) {
    return null;
  }

  return cleanUploads.length === 1 ? cleanUploads[0] : cleanUploads;
}

export function getNotificationUploads(notification) {
  return normalizePrintJobUploads(notification?.metadata?.printJobUploads || notification?.metadata?.print_job_uploads);
}

export function getPrintJobNotificationDownload(notification) {
  const metadata = notification?.metadata || {};
  const printJobStatus = metadata.printJobStatus || metadata.print_job_status;
  const quality = metadata.quality;
  const fileType = PRINT_JOB_QUALITY_FILE_TYPES[quality];
  const docIdentifier = getNotificationDocIdentifier(notification);
  const isSupportedEntity = docIdentifier === PRINT_JOB_DOWNLOAD_ENTITIES.edition || docIdentifier === PRINT_JOB_DOWNLOAD_ENTITIES.orderItem;
  const uploads = getNotificationUploads(notification);

  if (printJobStatus !== 'succeeded' || !fileType || !isSupportedEntity || !uploads) {
    return null;
  }

  return {
    files: uploads,
    fileType,
  };
}

export function getPrintJobNotificationEditionTitle(notification) {
  const metadata = notification?.metadata || {};
  if (metadata.editionTitle || metadata.edition_title) {
    return metadata.editionTitle || metadata.edition_title;
  }

  return getNotificationDocIdentifier(notification) === PRINT_JOB_DOWNLOAD_ENTITIES.edition
    ? getNotificationEntityId(notification)
    : null;
}

export function getPrintJobNotificationEditionId(notification) {
  const metadata = notification?.metadata || {};
  if (metadata.editionId || metadata.edition_id) {
    return metadata.editionId || metadata.edition_id;
  }

  return getNotificationDocIdentifier(notification) === PRINT_JOB_DOWNLOAD_ENTITIES.edition
    ? getNotificationEntityId(notification)
    : null;
}

export function getPrintJobNotificationQualityLabel(notification, messages = {}) {
  const labelKey = PRINT_JOB_QUALITY_LABEL_KEYS[notification?.metadata?.quality];
  return labelKey ? messages[labelKey] || messages.editor?.[labelKey] || notification?.metadata?.quality : notification?.metadata?.quality;
}

/**
 * Always return upload URLs as a list (single string → one-element array).
 *
 * @returns {string[]}
 */
export function getPrintJobNotificationFileList(download) {
  if (!download?.files) {
    return [];
  }

  return Array.isArray(download.files) ? download.files : [download.files];
}

/**
 * Basename labels for each upload (supports single- and multi-file print jobs).
 *
 * @returns {string[]}
 */
export function getPrintJobNotificationFileNames(download) {
  return getPrintJobNotificationFileList(download).map((fileUrl, index) => {
    const basename = (String(fileUrl).split('/').pop() || '').split('?')[0];
    return basename || `file-${index + 1}`;
  });
}

/**
 * PDF line for notification context: quality label, plus file count when multi-file.
 */
export function getPrintJobNotificationPdfSummary(notification, messages = {}) {
  const download = getPrintJobNotificationDownload(notification);
  if (!download) {
    return null;
  }

  const qualityLabel = getPrintJobNotificationQualityLabel(notification, messages);
  const files = getPrintJobNotificationFileList(download);
  if (files.length <= 1) {
    return qualityLabel;
  }

  const filesWord = messages.files || messages.editor?.files || 'files';
  return `${qualityLabel} (${files.length} ${filesWord})`;
}

export function isMatchingEditorRoute(notification, pathname = '') {
  if (getNotificationDocIdentifier(notification) !== PRINT_JOB_DOWNLOAD_ENTITIES.edition) {
    return false;
  }

  const entityId = getNotificationEntityId(notification);
  if (!entityId) {
    return false;
  }

  const match = pathname.match(/(?:^|\/)editor\/([^/?#]+)/);
  return match ? match[1] === entityId : false;
}
